// Shared CRM display helpers keyed to the design tokens/components.

export const BOARD_STAGE_LABELS = {
  new: "New",
  call_scheduled: "Call scheduled",
  // The call happened; nothing (pricing, a contract) has gone out yet.
  in_progress: "In progress",
  evaluating: "Deciding internally",
  // The stage KEY stays proposal_sent — it is in the database, in transitions and
  // in every proposal payload. Only the words change.
  proposal_sent: "Pricing sent",
  agreement: "Agreement + legal",
  closed_won: "Closed won",
  closed_lost: "Closed lost",
};

export const CURRENCY_SYMBOL = { USD: "$", EUR: "€", GBP: "£", ILS: "₪" };

// Compact money: 30000 → "$30K", 1500 → "$1.5K", 900 → "$900".
export function moneyK(value, currency) {
  if (value === null || value === undefined || value === "") return "—";
  const sym = CURRENCY_SYMBOL[currency] || "";
  const n = Number(value);
  if (Math.abs(n) >= 1000) {
    const k = n / 1000;
    const s = Number.isInteger(k) ? String(k) : k.toFixed(1);
    return `${sym}${s}K`;
  }
  return `${sym}${n}`;
}

// Source (contacts.lead_source) → locked brand pill. blue/pink/green only.
//
// This reads the STORED, normalized column — never the raw first_touch_source
// enum. Sniffing that enum's text here meant the pill showed HubSpot's guess
// and nothing else: OFFLINE and DIRECT_TRAFFIC (667 contacts) got no pill,
// SOCIAL_MEDIA was labelled "paid social", and the 82 contacts who told us
// they were referred were never read at all. There is no fallback colour: an
// unattributed contact (NULL) or any value outside the vocabulary returns null
// and the caller renders nothing. A wrong pill is worse than no pill.
//
// The class is still .crm-pill--paid-social — it is the blue token, and
// renaming a locked pill class is Gabriel's call in the design file.
const SOURCE_PILLS = {
  linkedin: { cls: "crm-pill--paid-social", label: "LinkedIn" },
  referral: { cls: "crm-pill--referral", label: "referral" },
  organic: { cls: "crm-pill--organic", label: "organic" },
};

export function sourcePill(source) {
  if (!source) return null;
  return SOURCE_PILLS[source] || null;
}

// Lifecycle stage → stage pill. lead = black outline, MQL mid-gray, SQL yellow,
// opportunity black, customer green. Stages a lead never reaches (subscriber,
// evangelist, other) fall back to the outlined treatment.
export const STAGE_PILLS = {
  lead: { cls: "crm-pill--lead", label: "lead" },
  marketingqualifiedlead: { cls: "crm-pill--mql", label: "MQL" },
  salesqualifiedlead: { cls: "crm-pill--sql", label: "SQL" },
  opportunity: { cls: "crm-pill--opportunity", label: "opportunity" },
  customer: { cls: "crm-pill--customer", label: "customer" },
};

// Not a lifecycle stage, so not in STAGE_PILLS (which is also the list of
// stages you can SET): a customer whose company has churned. See shownStage.
const CHURNED_PILL = { cls: "crm-pill--churned", label: "churned" };

export function stagePill(stage) {
  if (stage === "churned") return CHURNED_PILL;
  return STAGE_PILLS[stage] || { cls: "crm-pill--lead", label: stage || "—" };
}

// The stage a contact SHOWS. Churn is recorded on the company, and the
// contact's lifecycle keeps saying 'customer' because they did become one —
// the funnel reports count exactly that. Printed as the present, though, it
// is wrong, so anyone at a churned company reads "churned", whatever stage
// they were left at. Mirrors STAGE_SQL in src/leadViews.js, which the Stage
// filter matches.
export function shownStage(stage, companyStatus) {
  return companyStatus === "churned" ? "churned" : stage;
}

// lead_channel → the short label from the mockups. Channel pills are always
// plain black-outline: informational, never statusful.
export const CHANNEL_LABELS = {
  website_form: "form",
  intro_email: "intro",
  linkedin_dm: "LI DM",
  whatsapp_dm: "WA DM",
  other: "other",
};

/**
 * The channel to SHOW for a contact: what intake recorded, or else what the
 * migrated HubSpot form evidence derives (src/leadChannel.js — this is that
 * module's channelSql in JS, and the two must keep saying the same thing).
 *
 * Every pill goes through here rather than reading lead_channel, because the
 * Channel filter matches the same expression: a row the filter includes has to
 * be a row whose pill says so.
 */
export function leadChannel(c) {
  return c?.lead_channel || c?.lead_channel_derived || null;
}

/**
 * Where a channel pill's value came from, for the pill's tooltip. A derived
 * channel is a reading of the form the lead converted on, not something a
 * person recorded, and the record should be able to say which it is.
 */
export function leadChannelTitle(c) {
  if (c?.lead_channel) return "Recorded when the lead arrived";
  if (c?.lead_channel_derived) return "Derived from the form they submitted (HubSpot)";
  return undefined;
}

export function contactName(c) {
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return name || c.email || "(no name)";
}

// Bare relative age: "today" / "1d ago" / "12d ago" / "3mo ago".
export function ago(ts) {
  if (!ts) return "—";
  // Calendar days apart, not elapsed milliseconds: an event at 09:00 tomorrow is
  // "tomorrow" even though it is 20 hours away, and one at 23:00 tonight is
  // "today" even though it is 3 hours away. Comparing raw differences puts both
  // in the same bucket and reads as wrong.
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(new Date()) - startOfDay(new Date(ts))) / 86400000);

  // Booked meetings are dated in the FUTURE. Saying "today" for all of them —
  // which a `days <= 0` catch-all does — made three cards claim a meeting was
  // happening now when only one was.
  if (days < 0) {
    const ahead = -days;
    if (ahead === 1) return "tomorrow";
    return ahead < 7 ? `in ${ahead}d` : `in ${Math.floor(ahead / 7)}w`;
  }
  if (days === 0) return "today";
  if (days === 1) return "1d ago";
  return days < 30 ? `${days}d ago` : `${Math.floor(days / 30)}mo ago`;
}

// "Jul 8" — with the year appended once the date leaves the current one.
export function dateShort(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  const opts = { month: "short", day: "numeric" };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = "numeric";
  // en-US for month-first order: every mockup dates things "Jul 8", not "8 Jul".
  return d.toLocaleDateString("en-US", opts);
}

// "Jul 8, 2026" — the year always shows. For dates whose year carries meaning
// (first touch, won/churn dates) rather than recent-activity dates.
export function dateLong(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// "Jul 23, 2026 · 3:14 PM" — the exact moment, for expanded detail rows. The
// collapsed row only has room for "Jul 23"; the expansion is where the record
// stops rounding.
export function dateTimeLong(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return `${dateLong(d)} · ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

// companies.status → its label. Sentence case, lowercase for pill use.
export const COMPANY_STATUS_LABELS = {
  prospect: "prospect",
  client: "client",
  churned: "churned",
  lost: "lost",
  not_a_fit: "not a fit",
  advisory: "advisory",
  vendor: "vendor",
  recycled: "recycled",
};

// The "returning" badge's tooltip: the badge itself says one word, the title
// says which closed-out status it came back from and when. Both are derived
// server-side from stage_transitions — there is no stored returning flag.
export function returningTitle(company) {
  const label = COMPANY_STATUS_LABELS[company.returning_from] || company.returning_from || "closed";
  return company.returning_at
    ? `Returning — was ${label} ${dateLong(company.returning_at)}`
    : `Returning — was ${label}`;
}

// "call 3d ago" style, using the activity type + last_activity_at.
export function activityLabel(type, ts) {
  if (!ts) return "no activity";
  return `${type || "activity"} ${ago(ts)}`;
}

// The disqualified / lost row state is DERIVED from the contact's company —
// there is no contact-level field for it. Returns the tag to render in place of
// the stage pill, or null while the company is still live.
const EXIT_STATUS_LABEL = { not_a_fit: "disqualified", lost: "lost" };

export function companyExitTag(contact) {
  const label = EXIT_STATUS_LABEL[contact.company_status];
  if (!label) return null;
  const reason = (contact.company_status_reason || "").replace(/_/g, " ");
  return reason ? `${label} · ${reason}` : label;
}

/**
 * What `companies.monthly_value` MEANS, which depends on the relationship.
 *
 * One column, two meanings, straight from migration 004: for a prospect it is
 * THEIR monthly ad budget — what they spend, or told us on the call they would —
 * and for a client it is OUR monthly retainer. Nothing may print this number
 * without saying which, because the two are off by an order of magnitude in the
 * same direction every time: Medable's card announced their $25K ad budget as
 * our fee, in the largest type on the page (docs/CONVENTIONS.md).
 *
 * "Value" is the word to avoid. It reads as deal value — revenue — which this
 * column is not for a prospect and is only incidentally for a client.
 */
export function moneyMeaning(status) {
  return status === "client" ? "retainer" : "budget";
}

// The same fact, spelled out for a tooltip on a bare number.
export function moneyMeaningTitle(status) {
  return status === "client" ? "Our monthly retainer" : "Their monthly ad budget";
}

/**
 * OUR SIGNED FEE, as stored (migration 029), split into the lines it was typed
 * as.
 *
 * A structure, not a figure — "$5K for spend up to $25K" and "+15% on any spend
 * above that" are two lines of one quote, and this is the same shape the pricing
 * panel already renders for the emailed version (pricingQuote in
 * timelineModel.mjs). Nothing is parsed and no number is extracted: the whole
 * point of the TEXT column is that any single figure would be half the deal.
 *
 * Returns [] when nothing is stored, so callers test one thing.
 */
export function signedFeeLines(text) {
  return String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

/*
 * A hand-pasted agreement link, or nothing.
 *
 * Same completion rule as every other URL a person types into this CRM (httpUrl
 * in Facts.js): a bare host gets https://. Any OTHER scheme is refused rather
 * than linked — the field is typed by hand and a link is an executable thing, so
 * "the value looks like a URL" is not a good enough test to hang an href on.
 */
function pastedUrl(value) {
  const s = String(value || "").trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return null;
  return `https://${s.replace(/^\/+/, "")}`;
}

/**
 * WHERE "open the agreement" GOES, and what to call it — decided once so that no
 * surface decides it locally, exactly like moneyMeaning above.
 *
 * Two sources, in order:
 *
 *  1. `signed_agreement_url` — the document, pasted by hand (migration 029).
 *  2. `agreement_notice` — the Gmail message that announced the signature, which
 *     is what PandaDoc actually leaves behind here: it is parsed out of
 *     docs@email.pandadoc.net (src/watchers/pandadoc.js) and no document URL has
 *     ever reached this system. Built server-side on the one threadUrl
 *     vocabulary, in GET /companies/:id.
 *
 * THE SECOND IS LABELLED FOR WHAT IT IS. "Signed agreement" on a link that opens
 * a notification email is the same class of lie as calling their ad budget our
 * fee: it reads as the document, and it is a mail about the document.
 *
 * Returns null when there is neither — no link, rather than a link to something
 * that might be the wrong thing.
 */
export function agreementLink(company) {
  const pasted = pastedUrl(company && company.signed_agreement_url);
  if (pasted) return { url: pasted, label: "signed agreement" };
  const notice = company && company.agreement_notice;
  const url = notice && typeof notice.url === "string" ? notice.url : null;
  return url ? { url, label: "signature notification email" } : null;
}
